import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, TYPE, clamp, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Lang } from '../../../ui/text.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { SHARE_LEVELS, barLeft, judgeCut, shareTargets } from './logic.js';

/**
 * QUARTERMASTER · FAIR SHARES — cut each sliding bar where its light band says.
 *
 * The classic DIVIDE game (Divide.tsx), drawn as the thing being shared: a loaf on Earth, a power
 * cell in the city and on the surface, a ration bar in the war, flatbread under the sand. Each lies
 * on its own tray and slides from side to side at its own pace; a band of light rides on it where a
 * fair cut falls. Tap the bar in its band as it passes and it is cut there; once a bar is cut it
 * stops and the shares part. A tap on the bar well away from any band is the mistake (a tap just
 * past the band's edge still counts; a tap beside the bar is nothing). Rounds 1 and 2 are two bars
 * in halves, round 2 a little quicker with narrower bands; round 3 three bars in halves, quicker
 * again. (Bars cut in thirds are still in the rules; no round deals them since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): a small view state (`SharesView`), a pure `View`, the `Board` that
 * slides the bars on the game clock, and the `demo` the how-to card plays.
 */

const W = BOARD.w;
const H = BOARD.h;
/** The trays' margin at either side; a bar slides along the track between them. */
const PAD = 12;
const TRACK = W - 2 * PAD;
/** After a wrong cut, that bar ignores taps this long (one slip is one mistake, not three). */
const COOL_MS = 450;
/** A wrong cut's cross shows this long. */
const MISS_MS = 600;
/** A finished bar's shares part over this long. */
const PART_MS = 400;

type Thing = 'loaf' | 'cell' | 'ration' | 'flatbread';
const THING: Record<Era, Thing> = { stars: 'loaf', neon: 'cell', debris: 'ration', dust: 'flatbread', canopy: 'cell' };

/** Where the trays are: `n` of them, stacked in the middle of the board. */
export function trays(n: number) {
  const h = n >= 3 ? 84 : 108;
  const gap = n >= 3 ? 14 : 26;
  const top = (H - (n * h + (n - 1) * gap)) / 2;
  return Array.from({ length: n }, (_, i) => ({ y: top + i * (h + gap), h }));
}

/** The tray under a finger at `y`, or −1. */
export function trayAt(n: number, y: number): number {
  return trays(n).findIndex((row) => y >= row.y - 4 && y <= row.y + row.h + 4);
}

/** Which fair cut each cut made is, in order. */
const madeOf = (cuts: readonly number[], shares: number) => {
  const targets = shareTargets(shares);
  return cuts.map((at) => targets.reduce((best, t, i) => (Math.abs(at - t) < Math.abs(at - targets[best]!) ? i : best), 0));
};

/** Everything the trays show. */
export interface SharesView {
  /** The game clock, ms: a power cell's shimmer runs by it. */
  t: number;
  /** Halves (2) or thirds (3); a bar's length as a share of its track; half its light band as a share of the bar. */
  sh: number;
  bw: number;
  band: number;
  /** Each bar's left end on its track (0 to 1 − bw). */
  x: readonly number[];
  /** The cuts made in each bar, where they fell along it (0–1). */
  c: readonly (readonly number[])[];
  /** How far each finished bar's shares have parted (0–1). */
  pt: readonly number[];
  /** A wrong cut: which bar, where along it (0–1), and how long ago (ms). */
  miss: readonly [bar: number, at: number, age: number] | null;
  fx?: Fx | undefined;
}

/** The thing being shared, `w` by `h`, its top left at 0,0. */
function ThingArt({ thing, w, h, id, lang, t }: { thing: Thing; w: number; h: number; id: string; lang: Lang; t: number }) {
  if (thing === 'loaf') {
    return (
      <g>
        <ellipse cx={w / 2} cy={h * 0.96} rx={w * 0.48} ry={h * 0.07} fill="#000" opacity=".45" />
        <path
          d={`M${h * 0.34} ${h * 0.94} Q${h * 0.02} ${h * 0.94} ${h * 0.05} ${h * 0.55} Q${h * 0.1} ${h * 0.1} ${h * 0.62} ${h * 0.1} H${w - h * 0.62} Q${w - h * 0.1} ${h * 0.1} ${w - h * 0.05} ${h * 0.55} Q${w - h * 0.02} ${h * 0.94} ${w - h * 0.34} ${h * 0.94} Z`}
          fill={`url(#${id}loaf)`}
          stroke="#6b3a14"
          strokeWidth="1.2"
        />
        {Array.from({ length: 6 }, (_, k) => {
          const x = (w * (k + 0.9)) / 6.8;
          return <path key={k} d={`M${x - h * 0.16} ${h * 0.44} Q${x} ${h * 0.24} ${x + h * 0.18} ${h * 0.34}`} stroke="#fbe0a6" strokeWidth={h * 0.07} strokeLinecap="round" fill="none" opacity=".75" />;
        })}
        <path d={`M${h * 0.5} ${h * 0.2} H${w - h * 0.5}`} stroke="#fff4d6" strokeOpacity=".3" strokeWidth={h * 0.05} strokeLinecap="round" />
        {[0.18, 0.37, 0.55, 0.71, 0.86].map((f, k) => (
          <circle key={f} cx={w * f} cy={h * (0.62 + (k % 2) * 0.12)} r={h * 0.025} fill="#fff" opacity=".25" />
        ))}
      </g>
    );
  }
  if (thing === 'cell') {
    const cap = h * 0.3;
    const inner = w - cap * 1.9;
    // The charge's shimmer runs along it by the clock.
    const shimmer = cap * 0.95 + ((t / 3_400) % 1) * Math.max(0, inner - w * 0.14);
    return (
      <g>
        <ellipse cx={w / 2} cy={h * 0.96} rx={w * 0.48} ry={h * 0.06} fill="#000" opacity=".45" />
        <rect x={cap * 0.7} y={h * 0.12} width={w - cap * 1.4} height={h * 0.76} rx={h * 0.16} fill="#0a0f1c" stroke="#aab6c2" strokeWidth="1.2" />
        <rect x={cap * 0.95} y={h * 0.24} width={inner} height={h * 0.52} rx={h * 0.1} fill={`url(#${id}charge)`} />
        <rect x={shimmer} y={h * 0.24} width={w * 0.14} height={h * 0.52} fill="#ffffff" opacity=".16" />
        {Array.from({ length: 5 }, (_, k) => {
          const x = cap * 0.95 + (inner * (k + 1)) / 6;
          return <line key={k} x1={x} y1={h * 0.24} x2={x} y2={h * 0.76} stroke="#05070a" strokeOpacity=".45" strokeWidth="1.5" />;
        })}
        <rect x="0" y={h * 0.18} width={cap} height={h * 0.64} rx="4" fill={`url(#${id}metal)`} stroke="#3a4450" />
        <rect x={w - cap} y={h * 0.18} width={cap} height={h * 0.64} rx="4" fill={`url(#${id}metal)`} stroke="#3a4450" />
        <rect x={w - cap * 0.35} y={h * 0.36} width={cap * 0.35} height={h * 0.28} rx="2" fill="#dfe6ec" />
        <path d={`M${cap * 0.9} ${h * 0.17} H${w - cap * 0.9}`} stroke="#ffffff" strokeOpacity=".35" strokeWidth="1.4" />
        <text x={w - cap * 1.55} y={h * 0.62} textAnchor="middle" fontSize={h * 0.26} fontWeight="700" fontFamily={mono} fill="#05070a" opacity=".55">
          +
        </text>
      </g>
    );
  }
  if (thing === 'ration') {
    const end = h * 0.22;
    const zig = (x: number, dir: 1 | -1) => {
      const pts: string[] = [];
      for (let k = 0; k <= 6; k++) pts.push(`${x + dir * (k % 2 ? end : end * 0.35)} ${h * 0.14 + (k * h * 0.72) / 6}`);
      return pts.join(' L');
    };
    return (
      <g>
        <ellipse cx={w / 2} cy={h * 0.95} rx={w * 0.48} ry={h * 0.06} fill="#000" opacity=".45" />
        <path
          d={`M${end} ${h * 0.14} H${w - end} L${zig(w - end, 1)} L${w - end} ${h * 0.86} H${end} L${zig(end, -1).split(' L').reverse().join(' L')} Z`}
          fill={`url(#${id}foil)`}
          stroke="#5d676e"
          strokeWidth="1"
        />
        <rect x={w * 0.3} y={h * 0.14} width={w * 0.4} height={h * 0.72} fill="#6b7442" />
        <rect x={w * 0.3} y={h * 0.14} width={w * 0.4} height={h * 0.08} fill="#8a9458" />
        <text x={w / 2} y={h * 0.58} textAnchor="middle" fontSize={h * 0.2} fontWeight="700" letterSpacing="1" fontFamily={mono} fill="#ece4c4">
          {say(lang, 'RATION', 'RASJON')}
        </text>
        {[0.12, 0.2, 0.78, 0.86].map((f) => (
          <path key={f} d={`M${w * f} ${h * 0.2} l${h * 0.12} ${h * 0.6}`} stroke="#ffffff" strokeOpacity=".35" strokeWidth="1" />
        ))}
      </g>
    );
  }
  // flatbread, baked by hand under the sand
  return (
    <g>
      <ellipse cx={w / 2} cy={h * 0.92} rx={w * 0.48} ry={h * 0.07} fill="#000" opacity=".45" />
      <path
        d={`M${h * 0.2} ${h * 0.3} Q${w * 0.25} ${h * 0.14} ${w * 0.5} ${h * 0.2} Q${w * 0.78} ${h * 0.12} ${w - h * 0.18} ${h * 0.28} Q${w + h * 0.02} ${h * 0.55} ${w - h * 0.16} ${h * 0.8} Q${w * 0.7} ${h * 0.9} ${w * 0.45} ${h * 0.84} Q${w * 0.2} ${h * 0.92} ${h * 0.18} ${h * 0.78} Q${-h * 0.04} ${h * 0.55} ${h * 0.2} ${h * 0.3} Z`}
        fill={`url(#${id}bread)`}
        stroke="#7a4a1c"
        strokeWidth="1.2"
      />
      {[0.14, 0.3, 0.46, 0.62, 0.78].map((f) => (
        <path key={f} d={`M${w * f} ${h * 0.3} l${h * 0.22} ${h * 0.44}`} stroke="#6b3a14" strokeOpacity=".45" strokeWidth={h * 0.05} strokeLinecap="round" />
      ))}
      {[0.1, 0.24, 0.37, 0.52, 0.66, 0.83, 0.9].map((f, k) => (
        <ellipse key={f} cx={w * f} cy={h * (0.4 + (k % 3) * 0.13)} rx={h * 0.05} ry={h * 0.035} fill="#4a2a10" opacity=".55" />
      ))}
    </g>
  );
}

/** The trays, from their view state alone: the things sliding on them, their bands of light and the cuts. */
function View({ view, era, accent, lang, input }: ViewProps<SharesView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const thing = THING[era];
  const n = view.x.length;
  const shares = Math.round(view.sh);
  const targets = shareTargets(shares);
  const bw = view.bw * TRACK;
  const rows = trays(n);
  const made = view.c.reduce((s, cuts) => s + cuts.length, 0);
  const total = n * targets.length;
  const allDone = made >= total;
  const miss = view.miss && view.miss[2] < MISS_MS ? view.miss : null;
  const missBar = miss ? Math.round(miss[0]) : -1;
  const thingH = rows[0]!.h - 20;
  const charge = era === 'canopy' ? ['#e9ffc4', '#7fc86a'] : era === 'neon' ? ['#8ff0ff', '#ff4fd8'] : [skin.haze, skin.ink];
  const status = allDone
    ? { text: say(lang, '✓ FAIR SHARES', '✓ RETTFERDIG DELT'), tone: 'ok' as const }
    : miss
      ? { text: say(lang, 'NOT IN THE LIGHT', 'IKKE I LYSET'), tone: 'bad' as const }
      : { text: say(lang, 'CUTS', 'KUTT'), count: [made, total] as const, hint: say(lang, 'TAP IN THE LIGHT', 'TRYKK I LYSET') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}loaf`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0c274" />
          <stop offset=".55" stopColor="#c47f36" />
          <stop offset="1" stopColor="#7e4519" />
        </linearGradient>
        <linearGradient id={`${id}bread`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0d196" />
          <stop offset="1" stopColor="#b98142" />
        </linearGradient>
        <linearGradient id={`${id}foil`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f1f4f6" />
          <stop offset=".45" stopColor="#a4aeb5" />
          <stop offset="1" stopColor="#d3d9dd" />
        </linearGradient>
        <linearGradient id={`${id}metal`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dfe6ec" />
          <stop offset=".5" stopColor="#7d8a97" />
          <stop offset="1" stopColor="#3a4450" />
        </linearGradient>
        <linearGradient id={`${id}charge`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={charge[0]} />
          <stop offset="1" stopColor={charge[1]} />
        </linearGradient>
        <linearGradient id={`${id}band`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={accent} stopOpacity=".18" />
          <stop offset=".5" stopColor={accent} stopOpacity=".55" />
          <stop offset="1" stopColor={accent} stopOpacity=".18" />
        </linearGradient>
        <linearGradient id={`${id}tray`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#000" stopOpacity=".42" />
          <stop offset="1" stopColor="#000" stopOpacity=".2" />
        </linearGradient>
      </defs>

      {rows.map((row, i) => {
        const cuts = view.c[i] ?? [];
        const done = cuts.length >= targets.length;
        const cutDone = madeOf(cuts, shares);
        const x = PAD + (view.x[i] ?? 0) * TRACK;
        const h = thingH;
        const y = row.y + (row.h - h) / 2 - 1;
        const bounds = [0, ...[...cuts].sort((a, b) => a - b), 1];
        const part = clamp(view.pt[i] ?? 0, 0, 1);
        return (
          <g key={i}>
            {/* the tray the thing slides on */}
            <rect x={PAD / 2} y={row.y} width={W - PAD} height={row.h} rx="12" fill={`url(#${id}tray)`} stroke={skin.ink} strokeOpacity=".18" />
            <line x1={PAD} y1={row.y + row.h - 7} x2={W - PAD} y2={row.y + row.h - 7} stroke={skin.ink} strokeOpacity=".3" strokeWidth="2" />
            {done && (
              <text x={W - PAD - 4} y={row.y + 17} textAnchor="end" fontSize={TYPE.label} fontWeight="700" fontFamily={mono} fill={OK}>
                ✓
              </text>
            )}
            <g transform={`translate(${x.toFixed(2)} ${y.toFixed(1)})`}>
              {done ? (
                // Cut and done: the shares part a little.
                bounds.slice(1).map((b, k) => {
                  const a = bounds[k]!;
                  const shift = (k - (bounds.length - 2) / 2) * 9 * part;
                  return (
                    <g key={k} transform={`translate(${shift.toFixed(1)} 0)`}>
                      <clipPath id={`${id}p${i}-${k}`}>
                        <rect x={a * bw} y={-6} width={(b - a) * bw} height={h + 12} />
                      </clipPath>
                      <g clipPath={`url(#${id}p${i}-${k})`}>
                        <ThingArt thing={thing} w={bw} h={h} id={id} lang={lang} t={view.t} />
                      </g>
                    </g>
                  );
                })
              ) : (
                <ThingArt thing={thing} w={bw} h={h} id={id} lang={lang} t={view.t} />
              )}
              {/* the bands of light where a fair cut falls, riding on the thing */}
              {!done &&
                targets.map((t, k) =>
                  cutDone.includes(k) ? null : (
                    <g key={k}>
                      <rect x={(t - view.band) * bw} y={-6} width={view.band * 2 * bw} height={h + 12} fill={`url(#${id}band)`} />
                      <path
                        d={`M${(t - view.band) * bw} -6 V${h + 6} M${(t + view.band) * bw} -6 V${h + 6}`}
                        stroke={accent}
                        strokeOpacity=".75"
                        strokeWidth="1.5"
                      />
                      <line x1={t * bw} y1={-5} x2={t * bw} y2={h + 5} stroke={INK.hi} strokeOpacity=".85" strokeWidth="1.4" strokeDasharray="4 3" />
                      <path d={`M${t * bw - 5} -8 L${t * bw + 5} -8 L${t * bw} -2 Z`} fill={accent} />
                    </g>
                  ),
                )}
              {/* the cuts made so far */}
              {!done &&
                cuts.map((cut, k) => (
                  <g key={`c${k}`}>
                    <line x1={cut * bw} y1={-2} x2={cut * bw} y2={h + 2} stroke={INK.onFill} strokeWidth="4" />
                    <line x1={cut * bw} y1={-2} x2={cut * bw} y2={h + 2} stroke={accent} strokeWidth="1.6" />
                  </g>
                ))}
              {miss && missBar === i && (
                <g transform={`translate(${(miss[1] * bw).toFixed(1)} ${h / 2})`} opacity={1 - miss[2] / MISS_MS}>
                  <path d="M-8 -8 L8 8 M8 -8 L-8 8" stroke={BAD} strokeWidth="3.2" strokeLinecap="round" />
                </g>
              )}
            </g>
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = SHARE_LEVELS[level]!;
  const need = lv.shares - 1;
  const [rand] = useState(() => seeded(Date.now() + level * 7919));
  /** Each bar starts somewhere different in its swing. */
  const [phases] = useState(() => Array.from({ length: lv.bars }, () => rand() * Math.PI * 2));
  const cuts = useRef<number[][]>(Array.from({ length: lv.bars }, () => []));
  const made = useRef<number[][]>(Array.from({ length: lv.bars }, () => []));
  const doneAt = useRef<(number | null)[]>(Array.from({ length: lv.bars }, () => null));
  const lefts = useRef<number[]>(Array.from({ length: lv.bars }, () => (1 - lv.barWidth) / 2));
  const miss = useRef<{ bar: number; at: number; when: number } | null>(null);
  const coolUntil = useRef<number[]>(Array.from({ length: lv.bars }, () => 0));

  const round = useRound(props, (_dt, now) => {
    lefts.current.forEach((_, i) => {
      if (doneAt.current[i] === null) lefts.current[i] = barLeft(lv.barWidth, lv.periods[i]!, phases[i]!, now);
    });
  });

  const cut = (p: Pt) => {
    if (!round.live) return;
    const i = trayAt(lv.bars, p.y);
    if (i < 0) return;
    const now = round.time();
    if (doneAt.current[i] !== null || now < coolUntil.current[i]!) return;
    const verdict = judgeCut((p.x - PAD) / TRACK, lefts.current[i]!, lv, made.current[i]!);
    if (verdict.kind === 'off') return;
    if (verdict.kind === 'miss') {
      coolUntil.current[i] = now + COOL_MS;
      miss.current = { bar: i, at: verdict.at, when: now };
      round.miss();
      return;
    }
    made.current[i] = [...made.current[i]!, verdict.target];
    cuts.current[i] = [...cuts.current[i]!, verdict.at];
    if (made.current[i]!.length >= need) doneAt.current[i] = now;
    round.hit();
    const all = made.current.reduce((s, m) => s + m.length, 0);
    round.progress(all / (lv.bars * need));
    if (doneAt.current.every((d) => d !== null)) round.win();
  };

  const now = round.now;
  const m = miss.current;
  const view: SharesView = {
    t: q(now),
    sh: lv.shares,
    bw: lv.barWidth,
    band: lv.band,
    x: lefts.current.map((x) => q(x, 3)),
    c: cuts.current.map((list) => list.map((at) => q(at, 3))),
    pt: doneAt.current.map((d) => (d === null ? 0 : q(clamp((now - d) / PART_MS, 0, 1), 2))),
    miss: m && now - m.when < MISS_MS ? [m.bar, q(m.at, 3), q(now - m.when)] : null,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap each bar in its light', 'Trykk på hver stang i lyset'), down: cut }}
    />
  );
}

/**
 * The how-to, slid by round 1's own swing: the finger waits over the first bar and taps as the
 * light comes under it, the bar stops and parts; then the same on the second. Worked out with the
 * board's `barLeft` and keyed every tenth of a second.
 */
function demoShares(): DemoScript<SharesView> {
  const lv = SHARE_LEVELS[0]!;
  const ms = 7_000;
  const taps = [1_900, 4_200];
  /** Where each bar is mid-swing when its tap comes, so the light is moving under the finger. */
  const phases = [Math.PI + 0.3 - (2 * Math.PI * taps[0]!) / lv.periods[0]!, 2 * Math.PI + 0.4 - (2 * Math.PI * taps[1]!) / lv.periods[1]!];
  const cutAt = 0.52;
  const leftAt = (i: number, t: number) => barLeft(lv.barWidth, lv.periods[i]!, phases[i]!, Math.min(t, taps[i]!));
  const rows = trays(2);
  const keys: DemoKey<SharesView>[] = [];
  for (let t = 0; t <= ms; t += 100) {
    keys.push({
      at: t,
      view: {
        t: 0,
        sh: lv.shares,
        bw: lv.barWidth,
        band: lv.band,
        x: [0, 1].map((i) => q(leftAt(i, t), 3)),
        c: taps.map((at) => (t >= at ? [cutAt] : [])),
        pt: taps.map((at) => q(clamp((t - at) / PART_MS, 0, 1), 2)),
        miss: null,
        ...(t >= taps[1]! ? { fx: [q(clamp(1 - (t - taps[1]!) / 450, 0, 1), 2), 0] as const } : {}),
      },
    });
  }
  const finger: FingerKey[] = [
    { at: 0, x: 250, y: 300, hide: true },
    { at: 300, x: 250, y: 300, hide: false },
  ];
  taps.forEach((at, i) => {
    const x = q(PAD + (leftAt(i, at) + cutAt * lv.barWidth) * TRACK);
    const y = q(rows[i]!.y + rows[i]!.h / 2);
    finger.push({ at: at - 700, x, y }, { at, x, y, down: true }, { at: at + 160, x, y, down: false });
  });
  finger.push({ at: 5_400, x: 250, y: 300, hide: true });
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'WAIT FOR THE LIGHT', no: 'VENT PÅ LYSET' },
      { at: 1_300, en: 'TAP THE BAR IN THE LIGHT', no: 'TRYKK PÅ STANGEN I LYSET' },
      { at: 2_800, en: 'CUT EVERY BAR', no: 'DEL HVER STANG' },
    ],
  };
}

export const game = thirtyGame<SharesView>({
  Board,
  View,
  demo: demoShares(),
  words: {
    en: {
      instruction: 'Each bar slides. Tap it in its band of light to cut fair shares.',
      howMove: 'Tap a bar where its light band is, as it slides past.',
    },
    no: {
      instruction: 'Hver stang glir. Trykk i det lyse feltet for å dele den rettferdig.',
      howMove: 'Trykk på stangen der det lyse feltet er, mens den glir forbi.',
    },
  },
});
